import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiErrorDetails, ApiErrorResponse } from '../models/api-error.interface';
import { SKIP_ERROR_TOAST } from './skip-error-toast';

function extractErrorDetail(details: ApiErrorResponse['details']): string {
  if (typeof details === 'string') {
    return details;
  }
  if (details && typeof details === 'object') {
    const d = details as ApiErrorDetails;
    if (Array.isArray(d.message)) {
      return d.message.join(' ');
    }
    if (typeof d.message === 'string') {
      return d.message;
    }
  }
  return 'An unexpected error occurred. Please try again.';
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Expected failures (see SKIP_ERROR_TOAST) are passed on without a toast.
      if (req.context.get(SKIP_ERROR_TOAST)) {
        return throwError(() => error);
      }

      const body = error.error as ApiErrorResponse;
      const status = error.status;

      const ref = body?.correlationId ? ` (Ref: ${body.correlationId})` : '';

      if (status === 0) {
        // No HTTP response reached the server — almost always a connectivity
        // problem on the user's side (wifi/internet down or unreachable).
        messageService.add({
          severity: 'warn',
          summary: 'Connection problem',
          detail: "Can't reach Greezmate. Please check your internet connection and try again.",
          life: 8000,
        });
      } else if (status >= 400 && status < 500) {
        messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: extractErrorDetail(body?.details),
          life: 8000,
        });
      } else if (status >= 500) {
        messageService.add({
          severity: 'error',
          summary: 'Server Error',
          detail:
            'A server error occurred. This is not caused by your action — please try again or contact support.' +
            ref,
          life: 8000,
        });
      }

      return throwError(() => error);
    })
  );
};
